// useIdleLogout — fires `onIdle` after `timeoutMs` with no user activity in
// ANY tab of the app, not just this one.
//
// Activity is recorded to a shared cross-tab beacon, and the timeout consults
// it before firing: a superadmin tab left unfocused while the same person
// works in the Admin tab must NOT declare them idle — its logout clears
// shared storage and would kill every superadmin tab at once. Only a browser
// nobody is touching goes quiet in all tabs and logs out for real.

import { useEffect, useRef } from 'react';
import { recordActivity, remainingIdleMs, shouldIdleLogout } from '../utils/activityBeacon';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export default function useIdleLogout(timeoutMs, onIdle) {
  const timerRef = useRef(null);
  // Keep the latest callback without re-subscribing listeners on every render.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!timeoutMs) return undefined;

    const arm = (delay) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        // The local timer ran out — but was anyone active in another tab?
        if (shouldIdleLogout(timeoutMs)) {
          onIdleRef.current();
        } else {
          arm(remainingIdleMs(timeoutMs) || timeoutMs);
        }
      }, delay);
    };

    const reset = () => {
      recordActivity();
      arm(timeoutMs);
    };

    // Re-arm when the tab becomes visible again (background timers get
    // throttled) — but do NOT record activity: looking is not touching.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') arm(timeoutMs);
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);
    recordActivity();
    arm(timeoutMs);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, reset));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [timeoutMs]);
}
